import { useState, useEffect, useCallback } from "react";
import type {
  Session,
  SessionDetail,
  SetupEntry,
  SessionComparison,
} from "../types/session";
import { apiFetch } from "../apiFetch";

const ACTIVE_SESSION_POLL_MS = 3000;
const POLL_BACKOFF_MAX_MS = 30_000;

export function useSession() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [activeSession, setActiveSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSessions = useCallback(async (): Promise<boolean> => {
    try {
      const res = await apiFetch(`/api/sessions`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSessions(data.sessions);
      setError(null);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchActiveSession = useCallback(async (): Promise<boolean> => {
    try {
      const res = await apiFetch(`/api/sessions/active`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setActiveSession((data.session as Session | null) ?? null);
      setError(null);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return false;
    }
  }, []);

  // Poll with exponential backoff so a downed API doesn't get hammered
  // every 3s forever; delay doubles per failed round and resets on success.
  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    let delay = ACTIVE_SESSION_POLL_MS;

    const tick = async () => {
      // A hidden kiosk tab still ticks but skips the network round-trip;
      // the visibilitychange handler below refreshes immediately on return.
      if (document.visibilityState === "hidden") {
        timer = window.setTimeout(tick, delay);
        return;
      }
      const results = await Promise.all([fetchSessions(), fetchActiveSession()]);
      if (cancelled) return;
      delay = results.every(Boolean)
        ? ACTIVE_SESSION_POLL_MS
        : Math.min(delay * 2, POLL_BACKOFF_MAX_MS);
      timer = window.setTimeout(tick, delay);
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        window.clearTimeout(timer);
        tick();
      }
    };

    tick();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [fetchSessions, fetchActiveSession]);

  const createSession = useCallback(
    async (
      label: string,
      driverId?: number,
      copySetupFrom?: number,
      notes?: string,
    ) => {
      try {
        const res = await apiFetch(`/api/sessions`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ label, driverId, copySetupFrom, notes }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        const session: Session = await res.json();
        setActiveSession(session);
        await fetchActiveSession();
        await fetchSessions();
        return { ok: true as const, session };
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return { ok: false as const, error: msg };
      }
    },
    [fetchSessions],
  );

  const endSession = useCallback(
    async (id: number) => {
      try {
        const res = await apiFetch(`/api/sessions/${id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ endedAt: new Date().toISOString() }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        if (activeSession?.id === id) {
          setActiveSession(null);
        }
        await fetchActiveSession();
        await fetchSessions();
        return { ok: true as const };
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return { ok: false as const, error: msg };
      }
    },
    [fetchSessions, activeSession],
  );

  const assignDriver = useCallback(
    async (id: number, driverId: number | null, label?: string, notes?: string) => {
      try {
        const res = await apiFetch(`/api/sessions/${id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ driverId, label, notes }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        const session: Session = await res.json();
        if (activeSession?.id === id) {
          setActiveSession(session);
        }
        await fetchActiveSession();
        await fetchSessions();
        return { ok: true as const, session };
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return { ok: false as const, error: msg };
      }
    },
    [activeSession, fetchActiveSession, fetchSessions],
  );

  const saveSetup = useCallback(async (sessionId: number, entries: SetupEntry[]) => {
    try {
      const res = await apiFetch(`/api/sessions/${sessionId}/setup`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entries: entries.map((e) => ({
            category: e.category,
            parameter: e.parameter,
            value: e.value,
            unit: e.unit,
            sortOrder: e.sort_order,
          })),
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return { ok: true as const, entries: data.setup_entries as SetupEntry[] };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return { ok: false as const, error: msg };
    }
  }, []);

  const fetchSessionDetail = useCallback(async (id: number) => {
    try {
      const res = await apiFetch(`/api/sessions/${id}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: SessionDetail = await res.json();
      return { ok: true as const, detail: data };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return { ok: false as const, error: msg };
    }
  }, []);

  const compareSession = useCallback(async (idA: number, idB: number) => {
    try {
      const res = await apiFetch(`/api/sessions/${idA}/compare/${idB}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: SessionComparison = await res.json();
      return { ok: true as const, comparison: data };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return { ok: false as const, error: msg };
    }
  }, []);

  const deleteSession = useCallback(
    async (id: number) => {
      try {
        const res = await apiFetch(`/api/sessions/${id}`, { method: "DELETE" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        if (activeSession?.id === id) {
          setActiveSession(null);
        }
        await fetchSessions();
        return { ok: true as const };
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return { ok: false as const, error: msg };
      }
    },
    [fetchActiveSession, fetchSessions, activeSession],
  );

  const downloadAttachment = useCallback(async (path: string, fallbackName: string) => {
    try {
      const res = await apiFetch(path);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition");
      const match = disposition?.match(/filename="?([^";]+)"?/i);
      const fileName = match?.[1] ?? fallbackName;

      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);

      return { ok: true as const };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return { ok: false as const, error: msg };
    }
  }, []);

  const exportSessionCsvV3 = useCallback(
    (id: number) => downloadAttachment(`/api/sessions/${id}/export/csv-v3`, `session-${id}-csv-v3.csv`),
    [downloadAttachment],
  );

  const downloadSessionSummary = useCallback(
    (id: number) => downloadAttachment(`/api/sessions/${id}/summary.md`, `session-${id}-summary.md`),
    [downloadAttachment],
  );

  return {
    sessions,
    activeSession,
    loading,
    error,
    createSession,
    endSession,
    saveSetup,
    fetchSessionDetail,
    compareSession,
    deleteSession,
    assignDriver,
    exportSessionCsvV3,
    downloadSessionSummary,
    refresh: fetchSessions,
  };
}
